/**
 * File: assets/basari-prescription-form.js
 * Purpose: Manual Prescription UI — presentation + Controller wiring only.
 * Sprint: 1B.12 Phase B — Manual Prescription UI
 * Owner: Basari Theme / PDP
 * Dependencies: basari-prescription.js (Controller API), drawer host mount
 *
 * Rules:
 *   - Never write directly to lensFlow.manualPrescription
 *   - All updates go through PrescriptionController.update()
 *   - No business validation (hooks + ARIA placeholders only)
 *   - PD fields render dynamically from a single container
 */
(function (global) {
  'use strict';

  var BasariPrescriptionForm = global.BasariPrescriptionForm || {};
  var READY_ATTR = 'data-basari-rx-form-ready';
  var PD_MODES = {
    SINGLE: 'single',
    DUAL: 'dual'
  };

  function getPdModes() {
    if (global.BasariPrescription && global.BasariPrescription.PD_MODES) {
      return global.BasariPrescription.PD_MODES;
    }
    return PD_MODES;
  }

  function createEl(tag, className, attrs) {
    var el = document.createElement(tag);
    if (className) el.className = className;
    if (attrs) {
      var key;
      for (key in attrs) {
        if (Object.prototype.hasOwnProperty.call(attrs, key) && attrs[key] != null) {
          el.setAttribute(key, attrs[key]);
        }
      }
    }
    return el;
  }

  function setHidden(el, hidden) {
    if (!el) return;
    if (hidden) {
      el.setAttribute('hidden', '');
    } else {
      el.removeAttribute('hidden');
    }
  }

  /**
   * @param {HTMLElement} root - [data-basari-prescription-root]
   * @param {Object} options
   * @param {Function} options.getController - returns PrescriptionController
   * @param {Function} [options.onClose]
   * @param {Function} [options.onConfirm]
   * @param {HTMLElement} [options.eventTarget] - listens for framework events
   */
  function ManualPrescriptionUI(root, options) {
    this.root = root;
    this.options = options || {};
    this.form = null;
    this.cache = {};
    this.bound = false;
    this._syncing = false;
    this._pdMode = PD_MODES.SINGLE;
    this._onHostEvent = this.onHostEvent.bind(this);
    this._onFormEvent = this.onFormEvent.bind(this);
  }

  ManualPrescriptionUI.prototype.getController = function () {
    if (typeof this.options.getController === 'function') {
      return this.options.getController();
    }
    return null;
  };

  ManualPrescriptionUI.prototype.cacheSelectors = function () {
    var form = this.root.querySelector('[data-basari-rx-form]');
    this.form = form;
    this.cache.form = form;
    this.cache.pd = form ? form.querySelector('[data-basari-rx-pd]') : null;
    this.cache.pdToggle = form ? form.querySelector('[data-basari-rx-pd-toggle]') : null;
    this.cache.pdFields = form ? form.querySelector('[data-basari-rx-pd-fields]') : null;
    this.cache.notes = form ? form.querySelector('[data-basari-rx-field="notes"]') : null;
    this.cache.charCurrent = form ? form.querySelector('[data-basari-rx-char-current]') : null;
    this.cache.confirmBtn = form ? form.querySelector('[data-basari-rx-confirm]') : null;
    this.cache.closeBtn = form ? form.querySelector('[data-basari-rx-close]') : null;
  };

  ManualPrescriptionUI.prototype.mount = function () {
    if (!this.root || this.root.getAttribute(READY_ATTR) === 'true') {
      this.hydrateFromController();
      return this;
    }

    this.cacheSelectors();
    if (!this.form) return this;

    this.bindEvents();
    this.renderPdFields(this._pdMode);
    this.hydrateFromController();
    this.root.setAttribute(READY_ATTR, 'true');
    return this;
  };

  ManualPrescriptionUI.prototype.bindEvents = function () {
    if (this.bound || !this.form) return;

    this.form.addEventListener('input', this._onFormEvent);
    this.form.addEventListener('change', this._onFormEvent);
    this.form.addEventListener('click', this._onFormEvent);
    this.form.addEventListener('submit', this._onFormEvent);

    var eventTarget = this.options.eventTarget || this.root;
    if (eventTarget && eventTarget.addEventListener) {
      eventTarget.addEventListener('basari:prescription:updated', this._onHostEvent);
      eventTarget.addEventListener('basari:prescription:reset', this._onHostEvent);
      eventTarget.addEventListener('basari:prescription:initialized', this._onHostEvent);
      eventTarget.addEventListener('basari:prescription:validation-failed', this._onHostEvent);
      eventTarget.addEventListener('basari:prescription:validation-passed', this._onHostEvent);
    }

    this.bound = true;
  };

  ManualPrescriptionUI.prototype.onHostEvent = function (event) {
    if (!event || this._syncing) return;
    var type = event.type;
    var detail = event.detail || {};

    if (
      type === 'basari:prescription:updated' ||
      type === 'basari:prescription:reset' ||
      type === 'basari:prescription:initialized'
    ) {
      this.hydrateFromPrescription(detail.prescription || null);
      return;
    }

    if (type === 'basari:prescription:validation-failed') {
      this.applyValidationResult(detail.result || {});
      return;
    }

    if (type === 'basari:prescription:validation-passed') {
      this.clearValidation();
    }
  };

  ManualPrescriptionUI.prototype.onFormEvent = function (event) {
    if (!event || this._syncing) return;
    var target = event.target;
    if (!target) return;

    if (event.type === 'submit') {
      event.preventDefault();
      this.handleConfirm();
      return;
    }

    if (event.type === 'click') {
      if (target.closest('[data-basari-rx-confirm]')) {
        event.preventDefault();
        this.handleConfirm();
        return;
      }
      if (target.closest('[data-basari-rx-close]')) {
        event.preventDefault();
        this.handleClose();
        return;
      }
      return;
    }

    if (target.matches('[data-basari-rx-pd-toggle]')) {
      this.handlePdToggle(target);
      return;
    }

    if (target.matches('[data-basari-rx-field="notes"]')) {
      this.updateCharCount(target);
      this.patchController({ notes: target.value });
      return;
    }

    if (target.matches('[data-basari-rx-field]')) {
      this.handleScalarField(target);
      return;
    }

    if (target.matches('[data-basari-rx-eye][data-basari-rx-eye-field]')) {
      this.handleEyeField(target);
      return;
    }

    if (target.matches('[data-basari-rx-pd-input]')) {
      this.handlePdInput(target);
    }
  };

  ManualPrescriptionUI.prototype.patchController = function (patch) {
    var controller = this.getController();
    if (!controller || typeof controller.update !== 'function') return null;
    this._syncing = true;
    try {
      return controller.update(patch);
    } finally {
      this._syncing = false;
    }
  };

  ManualPrescriptionUI.prototype.handleScalarField = function (input) {
    var key = input.getAttribute('data-basari-rx-field');
    if (!key) return;
    var patch = {};
    patch[key] = input.value;
    this.patchController(patch);
  };

  ManualPrescriptionUI.prototype.handleEyeField = function (input) {
    var eye = input.getAttribute('data-basari-rx-eye');
    var field = input.getAttribute('data-basari-rx-eye-field');
    if (!eye || !field) return;
    var eyePatch = {};
    eyePatch[field] = input.value;
    var patch = {};
    patch[eye] = eyePatch;
    this.patchController(patch);
  };

  ManualPrescriptionUI.prototype.handlePdToggle = function (toggle) {
    var modes = getPdModes();
    var dual = !!toggle.checked;
    var mode = dual ? modes.DUAL || PD_MODES.DUAL : modes.SINGLE || PD_MODES.SINGLE;
    var controller = this.getController();
    var current =
      controller && typeof controller.getPrescription === 'function'
        ? controller.getPrescription()
        : null;

    toggle.setAttribute('aria-checked', dual ? 'true' : 'false');
    this._pdMode = mode;
    this.renderPdFields(mode);
    if (current && current.pd) {
      this.setPdValues(current.pd);
    }
    this.patchController({ pdMode: mode });
  };

  ManualPrescriptionUI.prototype.handlePdInput = function (input) {
    var key = input.getAttribute('data-basari-rx-pd-key');
    if (!key) return;
    var pdPatch = {};
    pdPatch[key] = input.value;
    this.patchController({ pd: pdPatch });
  };

  ManualPrescriptionUI.prototype.renderPdFields = function (mode) {
    var container = this.cache.pdFields;
    var pdRoot = this.cache.pd;
    if (!container || !pdRoot) return;

    var modes = getPdModes();
    var isDual = mode === (modes.DUAL || PD_MODES.DUAL);
    var labelSingle = pdRoot.getAttribute('data-label-pd') || 'PD';
    var labelRight = pdRoot.getAttribute('data-label-pd-right') || 'Right eye (OD)';
    var labelLeft = pdRoot.getAttribute('data-label-pd-left') || 'Left eye (OS)';
    var uid = (this.root.getAttribute('id') || 'BasariRxPd') + '-fields';

    container.innerHTML = '';

    if (isDual) {
      var grid = createEl('div', 'basari-rx-form__pd-grid');
      var corner = createEl('span', 'basari-rx-form__pd-grid-corner', { 'aria-hidden': 'true' });
      var headRight = createEl('span', 'basari-rx-form__pd-grid-head');
      headRight.textContent = labelRight;
      var headLeft = createEl('span', 'basari-rx-form__pd-grid-head');
      headLeft.textContent = labelLeft;
      var rowLabel = createEl('span', 'basari-rx-form__pd-grid-label');
      rowLabel.textContent = labelSingle;

      grid.appendChild(corner);
      grid.appendChild(headRight);
      grid.appendChild(headLeft);
      grid.appendChild(rowLabel);
      grid.appendChild(
        this.buildPdInputOnly({
          id: uid + '-pd-right',
          key: 'right',
          label: labelRight,
          name: 'pd_right'
        })
      );
      grid.appendChild(
        this.buildPdInputOnly({
          id: uid + '-pd-left',
          key: 'left',
          label: labelLeft,
          name: 'pd_left'
        })
      );
      container.appendChild(grid);
      container.classList.add('basari-rx-form__pd-fields--dual');
      container.classList.remove('basari-rx-form__pd-fields--single');
    } else {
      container.appendChild(
        this.buildPdField({
          id: uid + '-pd-single',
          key: 'single',
          label: labelSingle,
          name: 'pd'
        })
      );
      container.classList.add('basari-rx-form__pd-fields--single');
      container.classList.remove('basari-rx-form__pd-fields--dual');
    }

    this._pdMode = isDual ? modes.DUAL || PD_MODES.DUAL : modes.SINGLE || PD_MODES.SINGLE;
  };

  ManualPrescriptionUI.prototype.buildPdInputOnly = function (config) {
    var wrap = createEl('div', 'basari-rx-form__pd-grid-cell');
    var errorId = config.id + '-error';
    var label = createEl('label', 'visually-hidden', { for: config.id });
    label.textContent = config.label;
    var input = createEl('input', 'basari-rx-form__input', {
      type: 'text',
      inputmode: 'decimal',
      id: config.id,
      name: config.name,
      autocomplete: 'off',
      'data-basari-rx-pd-input': '',
      'data-basari-rx-pd-key': config.key,
      'aria-invalid': 'false',
      'aria-describedby': errorId
    });
    var error = createEl('span', 'basari-rx-form__error basari-rx-form__error--inline', {
      id: errorId,
      'data-basari-rx-error': 'pd.' + config.key,
      role: 'alert'
    });
    setHidden(error, true);
    wrap.appendChild(label);
    wrap.appendChild(input);
    wrap.appendChild(error);
    return wrap;
  };

  ManualPrescriptionUI.prototype.buildPdField = function (config) {
    var wrap = createEl('div', 'basari-rx-form__field basari-rx-form__field--pd');
    var label = createEl('label', 'basari-rx-form__label', { for: config.id });
    var labelText = createEl('span', 'basari-rx-form__label-text');
    labelText.textContent = config.label;
    label.appendChild(labelText);

    var errorId = config.id + '-error';
    var input = createEl('input', 'basari-rx-form__input', {
      type: 'text',
      inputmode: 'decimal',
      id: config.id,
      name: config.name,
      autocomplete: 'off',
      'data-basari-rx-pd-input': '',
      'data-basari-rx-pd-key': config.key,
      'aria-invalid': 'false',
      'aria-describedby': errorId
    });

    var error = createEl('p', 'basari-rx-form__error', {
      id: errorId,
      'data-basari-rx-error': 'pd.' + config.key,
      role: 'alert'
    });
    setHidden(error, true);

    wrap.appendChild(label);
    wrap.appendChild(input);
    wrap.appendChild(error);
    return wrap;
  };

  ManualPrescriptionUI.prototype.updateCharCount = function (textarea) {
    if (!textarea || !this.cache.charCurrent) return;
    this.cache.charCurrent.textContent = String((textarea.value || '').length);
  };

  ManualPrescriptionUI.prototype.hydrateFromController = function () {
    var controller = this.getController();
    if (!controller || typeof controller.getPrescription !== 'function') return;
    this.hydrateFromPrescription(controller.getPrescription());
  };

  ManualPrescriptionUI.prototype.hydrateFromPrescription = function (prescription) {
    if (!this.form || !prescription) return;

    this._syncing = true;
    try {
      var modes = getPdModes();
      var mode =
        prescription.pdMode === (modes.DUAL || PD_MODES.DUAL)
          ? modes.DUAL || PD_MODES.DUAL
          : modes.SINGLE || PD_MODES.SINGLE;

      if (this.cache.pdToggle) {
        var dual = mode === (modes.DUAL || PD_MODES.DUAL);
        this.cache.pdToggle.checked = dual;
        this.cache.pdToggle.setAttribute('aria-checked', dual ? 'true' : 'false');
      }

      if (this._pdMode !== mode || !this.cache.pdFields || !this.cache.pdFields.children.length) {
        this.renderPdFields(mode);
      } else {
        this._pdMode = mode;
      }

      this.setFieldValue('patientName', prescription.patientName);
      this.setFieldValue('notes', prescription.notes);
      this.setFieldValue('dob', prescription.dob);
      this.setFieldValue('gender', prescription.gender);

      this.setEyeValues('rightEye', prescription.rightEye);
      this.setEyeValues('leftEye', prescription.leftEye);
      this.setPdValues(prescription.pd || {});

      if (this.cache.notes) {
        this.updateCharCount(this.cache.notes);
      }

      if (prescription.validation && prescription.validation.errors) {
        this.applyValidationErrors(prescription.validation.errors);
      }
    } finally {
      this._syncing = false;
    }
  };

  ManualPrescriptionUI.prototype.setFieldValue = function (key, value) {
    var el = this.form.querySelector('[data-basari-rx-field="' + key + '"]');
    if (!el) return;
    var next = value == null ? '' : String(value);
    if (el.value !== next) el.value = next;
  };

  ManualPrescriptionUI.prototype.setEyeValues = function (eyeKey, eye) {
    if (!eye) return;
    var inputs = this.form.querySelectorAll('[data-basari-rx-eye="' + eyeKey + '"]');
    var i;
    for (i = 0; i < inputs.length; i += 1) {
      var field = inputs[i].getAttribute('data-basari-rx-eye-field');
      if (!field) continue;
      var next = eye[field] == null ? '' : String(eye[field]);
      if (inputs[i].value !== next) inputs[i].value = next;
    }
  };

  ManualPrescriptionUI.prototype.setPdValues = function (pd) {
    var inputs = this.form.querySelectorAll('[data-basari-rx-pd-input]');
    var i;
    for (i = 0; i < inputs.length; i += 1) {
      var key = inputs[i].getAttribute('data-basari-rx-pd-key');
      if (!key) continue;
      var next = pd[key] == null ? '' : String(pd[key]);
      if (inputs[i].value !== next) inputs[i].value = next;
    }
  };

  ManualPrescriptionUI.prototype.clearValidation = function () {
    if (!this.form) return;
    var errors = this.form.querySelectorAll('[data-basari-rx-error]');
    var invalids = this.form.querySelectorAll('[aria-invalid="true"]');
    var i;
    for (i = 0; i < errors.length; i += 1) {
      errors[i].textContent = '';
      setHidden(errors[i], true);
    }
    for (i = 0; i < invalids.length; i += 1) {
      invalids[i].setAttribute('aria-invalid', 'false');
    }
  };

  ManualPrescriptionUI.prototype.applyValidationResult = function (result) {
    this.clearValidation();
    if (!result || !result.errors) return;
    this.applyValidationErrors(result.errors);
  };

  /**
   * Future ValidationService hook — maps error keys to [data-basari-rx-error].
   * Keys may be flat ("patientName") or nested ("rightEye.sphere", "pd.single").
   */
  ManualPrescriptionUI.prototype.applyValidationErrors = function (errors) {
    if (!errors || !this.form) return;
    var key;
    for (key in errors) {
      if (!Object.prototype.hasOwnProperty.call(errors, key)) continue;
      var message = errors[key];
      if (message == null || message === '') continue;
      var errorEl = this.form.querySelector('[data-basari-rx-error="' + key + '"]');
      if (!errorEl) continue;
      errorEl.textContent = String(message);
      setHidden(errorEl, false);

      var control = this.findControlForErrorKey(key);
      if (control) control.setAttribute('aria-invalid', 'true');
    }
  };

  ManualPrescriptionUI.prototype.findControlForErrorKey = function (key) {
    if (!this.form || !key) return null;
    if (key.indexOf('rightEye.') === 0 || key.indexOf('leftEye.') === 0) {
      var parts = key.split('.');
      return this.form.querySelector(
        '[data-basari-rx-eye="' + parts[0] + '"][data-basari-rx-eye-field="' + parts[1] + '"]'
      );
    }
    if (key.indexOf('pd.') === 0) {
      return this.form.querySelector('[data-basari-rx-pd-key="' + key.split('.')[1] + '"]');
    }
    return this.form.querySelector('[data-basari-rx-field="' + key + '"]');
  };

  ManualPrescriptionUI.prototype.handleConfirm = function () {
    var controller = this.getController();
    if (controller && typeof controller.validate === 'function') {
      controller.validate({ scope: 'complete' });
    }
    if (controller && typeof controller.save === 'function') {
      controller.save();
    }
    if (typeof this.options.onConfirm === 'function') {
      this.options.onConfirm(controller ? controller.getPrescription() : null);
    }
  };

  ManualPrescriptionUI.prototype.handleClose = function () {
    if (typeof this.options.onClose === 'function') {
      this.options.onClose();
    }
  };

  ManualPrescriptionUI.prototype.refresh = function () {
    this.hydrateFromController();
  };

  function mount(root, options) {
    if (!root) return null;
    var ui = new ManualPrescriptionUI(root, options || {});
    return ui.mount();
  }

  BasariPrescriptionForm.ManualPrescriptionUI = ManualPrescriptionUI;
  BasariPrescriptionForm.mount = mount;
  BasariPrescriptionForm.PRESCRIPTION_FIELDS = ['sphere', 'cylinder', 'axis', 'add'];

  global.BasariPrescriptionForm = BasariPrescriptionForm;
})(typeof window !== 'undefined' ? window : this);
